import { useState } from "react";
import { toast } from "react-toastify";
import { upload_file_to_s3 } from "../../utils";
import { useUploadResignationDocumentsMutation } from "../../api/resignationApi";
import { MAX_FILE_SIZE_BYTES, apiErrorMessage } from "./constants";
import DocumentList from "./DocumentList";

const UploadDocuments = ({ employeeId, rejectionReason, onBehalf = false }) => {
  const [staged, setStaged] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadDocuments, { isLoading: isSubmitting }] = useUploadResignationDocumentsMutation();

  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = ""; // let the same file be re-picked after a removal
    if (!files.length) return;

    const oversized = files.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (oversized.length) {
      toast.error("File size limit is 10Mb");
      return;
    }

    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const response = await upload_file_to_s3(file);
        const s3Document = response?.data?.data;
        const objectKey = s3Document?.fileName || s3Document?.key;
        if (!objectKey) {
          toast.error(`Failed to upload ${file.name}`);
          continue;
        }
        uploaded.push({
          fileName: file.name,
          fileUrl: objectKey,
          uploadedDate: new Date().toISOString(),
        });
      }
      if (uploaded.length) {
        setStaged((prev) => [...prev, ...uploaded]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload the file");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (index) => {
    setStaged((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!staged.length) {
      toast.error("Please attach at least one document.");
      return;
    }
    try {
      await uploadDocuments({
        files: staged,
        ...(onBehalf && employeeId ? { employeeId } : {}),
      }).unwrap();
      setStaged([]);
      toast.success("Exit documents submitted for review.");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to submit the documents."));
    }
  };

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="text-lg font-semibold text-gray-800">
        {onBehalf ? "Upload Documents on Behalf" : "Exit Clearance Documents"}
      </h2>
      <p className="mt-1 text-sm text-gray-500">
        {onBehalf
          ? "Attach the employee's signed exit-clearance documents. They go to review exactly as if the employee had uploaded them."
          : "Attach your signed exit-clearance documents. You can add more than one file."}
      </p>

      {rejectionReason && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm font-medium text-red-700">
            HR sent your documents back for correction
          </p>
          <p className="mt-1 text-sm text-red-600">{rejectionReason}</p>
        </div>
      )}

      <label
        className={`mt-4 flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center hover:border-blue-primary hover:bg-blue-secondery-2 ${
          uploading ? "pointer-events-none opacity-60" : ""
        }`}
      >
        <input type="file" multiple className="hidden" onChange={handleFiles} disabled={uploading} />
        <span className="text-sm font-medium text-blue-primary">
          {uploading ? "Uploading..." : "Click to choose files"}
        </span>
        <span className="mt-1 text-xs text-gray-400">Up to 10Mb per file</span>
      </label>

      <div className="mt-4">
        <DocumentList
          documents={staged}
          onRemove={handleRemove}
          emptyText="No files attached yet."
        />
      </div>

      <div className="mt-5 flex justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!staged.length || uploading || isSubmitting}
          className="rounded-lg bg-blue-primary px-5 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
        >
          {isSubmitting ? "Submitting..." : "Submit Documents"}
        </button>
      </div>
    </section>
  );
};

export default UploadDocuments;
